// EventSource wrapper for GET /api/tables/{table}/events (DESIGN.md section 7.5).
// Event `state` carries the full GET /api/tables/{table} body; `heartbeat` every 15s keeps
// the connection alive through nginx. We manage reconnection ourselves (instead of relying on
// the browser's built-in EventSource retry) so we can back off and surface connection status
// to the UI (the "backend unreachable" banner, DESIGN.md section 9.2).
import type { TableState } from './types'

export interface TableEventsHandle {
  close: () => void
}

const BACKOFF_START_MS = 1000
const BACKOFF_MAX_MS = 15000

export function subscribeTableEvents(
  table: string,
  onState: (state: TableState) => void,
  onConnectionChange?: (connected: boolean) => void,
): TableEventsHandle {
  let es: EventSource | null = null
  let closed = false
  let backoff = BACKOFF_START_MS
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  function connect() {
    if (closed) return
    es = new EventSource(`/api/tables/${encodeURIComponent(table)}/events`)

    es.addEventListener('open', () => {
      backoff = BACKOFF_START_MS
      onConnectionChange?.(true)
    })

    es.addEventListener('state', (evt) => {
      const msgEvt = evt as MessageEvent<string>
      try {
        const state = JSON.parse(msgEvt.data) as TableState
        onState(state)
      } catch {
        // malformed payload — ignore this tick, next one will self-correct
      }
    })

    // Heartbeats need no handling beyond keeping the connection alive; named listener so it
    // doesn't fall through to onerror handling in browsers that treat unknown events oddly.
    es.addEventListener('heartbeat', () => {})

    es.addEventListener('error', () => {
      onConnectionChange?.(false)
      es?.close()
      es = null
      if (closed) return
      retryTimer = setTimeout(connect, backoff)
      backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
    })
  }

  connect()

  return {
    close() {
      closed = true
      if (retryTimer) clearTimeout(retryTimer)
      es?.close()
      es = null
    },
  }
}
